<template>
  <div
    v-if="!showProductTour"
    :class="$style.container"
    data-test-id="NavPopover__container"
  >
    <div ref="tooltip">
      <div :style="tooltipPosition">
        <div
          :class="{
            [$style.tooltipContent]: true,
            [$style.tooltipArrowBottom]: arrowPlacement === 'bottom',
            [$style.extraWidth]: showSteps,
          }"
        >
          <CloseButton
            v-if="!showSteps"
            :class="$style.closeButton"
            :handleClose="handleClose"
          />
          <h4 :class="$style.headerText">
            {{ headerText }}
          </h4>
          <p
            :class="{
              [$style.bodyText]: true,
              [$style.extraPadding]: showSteps
            }"
          >
            {{ bodyText }}
          </p>
          <footer
            v-if="!showSteps"
            :class="$style.footer"
          >
            <div :class="$style.buttonContainer">
              <FlatActionButton
                v-if="isButton"
                :action="handleAction"
                :buttonClass="['btnDefault', 'btnReverse']"
                :text="buttonText"
              />
              <FlatActionAnchor
                v-else
                :buttonStyles="['btnDefault', 'btnReverse']"
                href="#/home"
                target="_self"
                :text="buttonText"
                @click="handleAction"
              />
              <FlatActionButton
                :action="() => handleClose('okBtn')"
                :buttonClass="['btnDefault', 'btnTransparent', 'btnReverse']"
                text="Ok, got it"
              />
            </div>
          </footer>
          <footer v-else>
            <div
              :class="{
                [$style.stepButtonContainer]: true,
                [$style.lastStepButtonContainer]: lastStep,
              }"
            >
              <FlatActionAnchor
                v-if="!lastStep"
                :buttonStyles="['btnDefault', 'btnTransparent', $style.noPadding]"
                href="#/users"
                target="_self"
                text="skip this tour"
                @click="handleClose"
              />
              <FlatActionAnchor
                :buttonStyles="['btnDefault', 'btnReverse']"
                :href="href"
                target="_self"
                :text="buttonText"
                @click="handleAction"
              />
            </div>
            <div
              v-if="showSteps"
              :class="$style.stepContainer"
            >
              <ul :class="$style.circleList">
                <li
                  v-for="(value, index) in stepImageMap"
                  :key="index"
                >
                  <div
                    :class="[$style.circle, {
                      [$style.circleComplete]: value,
                    }]"
                    @click="handleDotsClick"
                  />
                </li>
              </ul>
            </div>
          </footer>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import CloseButton from 'components/Buttons/CloseButton.vue';
import EventHelper from 'client/GrowthEngineering/EventHelper';
import FlatActionAnchor from 'components/Buttons/FlatActionAnchor.vue';
import FlatActionButton from 'components/Buttons/FlatActionButton.vue';

const onboardingModule = 'OnboardingModule';

export default {
  name: 'NavPopover',

  components: {
    CloseButton,
    FlatActionAnchor,
    FlatActionButton,
  },

  props: {
    arrowPlacement: {
      type: String,
      default: 'top',
    },
    bodyText: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: "Let's go!",
    },
    handleAction: {
      type: Function,
      default: () => {},
    },
    handleClose: {
      type: Function,
      default: () => {},
    },
    headerText: {
      type: String,
      default: '',
    },
    href: {
      type: String,
      default: '',
    },
    isButton: Boolean,
    numSteps: {
      type: Number,
      default: 0,
    },
    offset: {
      type: Object,
      default: () => ({}),
    },
    placement: {
      type: String,
      default: 'bottom',
    },
    showSteps: {
      type: Boolean,
      default: false,
    },
    stepIndex: {
      type: Number,
      default: 0,
    },
  },

  computed: {
    // NavPopovers will be suppressed during the product tour
    ...mapState(onboardingModule, [
      'showProductTour',
    ]),

    lastStep() {
      return this.stepIndex === this.numSteps - 1;
    },

    stepImageMap() {
      const map = [];
      for (let i = 0; i < this.numSteps; i += 1) {
        map.push(i <= this.stepIndex);
      }
      return map;
    },

    tooltipPosition() {
      return {
        left: `${this.offset.left || 0}px`,
        position: 'absolute',
        top: `${this.offset.top || 0}px`,
      };
    },
  },

  methods: {
    // For data gathering, only trigger an event here
    handleDotsClick() {
      EventHelper.triggerProgressDotsClicked({
        popoverTitle: this.headerText,
      });
    },
  },
};
</script>

<style module>
@value tooltipSize: 16px;
@value tooltipSizeDouble: 28px;
@value tooltipSizeNegative: -16px;

.container {
  left: 0;
  position: fixed;
  top: 0;
}

/* ----------- Tooltip --------- */
.tooltipContent {
  background-color: var(--jc-color-informational-base);
  border-radius: 5px;
  color: white;
  left: -28px;
  padding: 24px;
  position: absolute;
  width: 300px;
}

.extraWidth {
  width: 350px;
}

/* ----------- Tooltip Arrow --------- */
.tooltipContent::after {
  border-bottom: tooltipSize solid transparent;
  border-right: tooltipSizeDouble solid var(--jc-color-informational-base);
  border-top: tooltipSize solid transparent;
  content: '';
  height: 0;
  left: tooltipSizeNegative;
  position: absolute;
  top: 20px;
  width: 0;
}

.tooltipArrowBottom::after {
  bottom: 20px;
  top: auto;
}

/* ----------- Tooltip Content --------- */
.headerText {
  font-size: 18px !important;
  font-weight: 600 !important;
  line-height: 1.325 !important;
  padding-right: 30px;
}

.bodyText {
  white-space: normal;
}

.closeButton {
  color: white;
  position: absolute;
  right: 14px;
  top: 14px;
}

.closeButton:hover {
  color: var(--jc-color-informational-base);
}

.footer {
  display: flex !important;
  font-size: 12px;
  justify-content: space-between;
}

.buttonContainer {
  align-items: center;
  color: var(--jc-color-informational-base);
  display: flex;
  justify-content: center;
  margin-top: 25px;
  text-align: center;
}

.extraPadding {
  padding-top: 10px;
}

.noPadding {
  padding: 0;
}

.gifContainer {
  padding-bottom: 20px;
}

.gif {
  width: 100%;
}

/* ----------- Step Content --------- */
.stepButtonContainer {
  color: var(--jc-color-informational-base);
  display: flex;
  justify-content: space-between;
}

.lastStepButtonContainer {
  justify-content: flex-end;
}

.stepContainer {
  padding-top: 20px;
  text-align: center;
}

.circleList {
  display: inline-block;
  list-style-type: none;
  margin: 0 !important;
  padding: 0 !important;
}

.circleList li { display: inline-block !important; }

.circle {
  background-color: white;
  border-radius: 50%;
  display: inline-block;
  height: 8px;
  margin-right: 3px;
  opacity: 50%;
  width: 8px;
}

.circleComplete {
  opacity: 100%;
}
</style>
